import type { QueryClient, QueryKey } from '@tanstack/react-query'

/**
 * What every screen that shows stock or an inventory document must refresh
 * after a change — the frontend half of "connected parts listen". One map, so
 * a sale, a receipt, a transfer or a count refreshes the same screens whoever
 * made it: stock lists, movements, lots, reports, the dashboard, sales
 * availability and reservations, alerts, approvals and notifications.
 *
 * Before WP01 each mutation refreshed only its own list: reserving a sale left
 * stock stale, a goods receipt left the purchase order and stock stale, and a
 * stock adjustment refreshed nothing. A prefix invalidation is cheap — only
 * screens on view refetch — so the families are deliberately wide.
 *
 * A push channel added later calls invalidateInventory() from its handler;
 * nothing here assumes the change came from this browser.
 */

export type InventoryChange =
  /** a transfer was raised or edited; no stock moved */
  | 'transfer.drafted'
  /**
   * approved, rejected or cancelled. Approving holds the transfer's source
   * stock and cancelling releases it (WP03), so stock screens refresh too.
   */
  | 'transfer.decided'
  /** picked, dispatched or received: stock moved */
  | 'transfer.moved'
  /** a count line was recorded, taken back, added or generated */
  | 'count.line'
  /** submitted, rejected or cancelled; no stock moved */
  | 'count.decided'
  /** approved and posted: stock moved */
  | 'count.applied'
  /** any other stock write: an adjustment, receipt, return, sale, reservation, lot change */
  | 'stock.moved'
  /** a warehouse's reorder point (or other threshold) for a product changed (WP02) */
  | 'stock.threshold'
  /** an alert rule was switched on or off, or re-addressed (WP02) */
  | 'alert.rule'
  /** an alert was taken by someone (WP02) */
  | 'alert.assigned'

function stockFamily(companyId: string): QueryKey[] {
  return [
    ['inventory', companyId, 'stock-balances'],
    ['inventory', companyId, 'stock-movements'],
    ['inventory', companyId, 'stock-movement'],
    ['inventory', companyId, 'lots'],
    ['inventory', companyId, 'lot'],
    ['inventory', companyId, 'reference', 'lots'],
    ['inventory', companyId, 'reports'],
    ['inventory', companyId, 'adjustment'],
    // Damage, scrap and expiry write-offs: an expired-lot disposal made elsewhere lists here too (WP04).
    ['inventory', companyId, 'stock-disposals'],
    // What is free at a transfer's source: the approve dialog and the form's hint (WP03).
    ['inventory', companyId, 'transfer-availability'],
    ['inventory', companyId, 'stock-free'],
    // The dashboard summary and the sales order's availability panel.
    ['companies', companyId, 'inventory'],
    ['companies', companyId, 'sales', 'reservations'],
    // Alerts open and close on the server as stock moves (StockLevelsChanged):
    // the generic list and the alerts page's stock list (WP02).
    ...alertLists(companyId),
  ]
}

const alertLists = (companyId: string): QueryKey[] => [
  ['core', 'alerts', companyId],
  ['inventory', companyId, 'stock-alerts'],
]

const transfers = (companyId: string): QueryKey => ['inventory', companyId, 'transfers']
const counts = (companyId: string): QueryKey => ['inventory', companyId, 'count-sessions']
const approvals: QueryKey = ['approvals']
const notifications: QueryKey = ['core', 'notifications']

export function inventoryChangeKeys(companyId: string, change: InventoryChange): QueryKey[] {
  switch (change) {
    case 'transfer.drafted':
      return [transfers(companyId)]
    case 'transfer.decided':
      return [transfers(companyId), approvals, notifications, ...stockFamily(companyId)]
    case 'transfer.moved':
      return [transfers(companyId), notifications, ...stockFamily(companyId)]
    case 'count.line':
      return [counts(companyId)]
    case 'count.decided':
      return [counts(companyId), approvals, notifications]
    case 'count.applied':
      return [counts(companyId), approvals, notifications, ...stockFamily(companyId)]
    case 'stock.moved':
      return stockFamily(companyId)
    // A threshold re-judges "low" on the server at once; the replenishment
    // report reads the same thresholds.
    case 'stock.threshold':
      return [...alertLists(companyId), ['inventory', companyId, 'reports']]
    // Switching a rule off closes its open alerts on the server.
    case 'alert.rule':
      return [['core', 'alert-rules', companyId], ...alertLists(companyId)]
    case 'alert.assigned':
      return alertLists(companyId)
  }
}

export async function invalidateInventory(
  queryClient: QueryClient,
  companyId: string | null,
  change: InventoryChange,
): Promise<void> {
  if (!companyId) return
  await Promise.all(
    inventoryChangeKeys(companyId, change).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
}

/**
 * The change an approval decision made, by the approvable's kind, so the
 * inbox and the approval banner refresh the document they decided. An
 * approved count is applied in the same request, so it moves stock.
 */
export function changeForApprovable(kind: string): InventoryChange | null {
  switch (kind) {
    case 'stock_transfer':
      return 'transfer.decided'
    case 'stock_count_session':
      return 'count.applied'
    case 'lot_quality_hold':
      return 'stock.moved'
    default:
      return null
  }
}
